import { useState } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { format, isValid } from "date-fns";
import { da } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { 
  FileText, 
  ExternalLink, 
  User, 
  Globe, 
  Tag, 
  ChevronDown,
  ChevronUp,
  BarChart,
  Calendar,
  Clock,
  DollarSign,
  Briefcase,
  RefreshCw
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { safeParseDate } from "@/utils/dateValidation";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/utils/format";

interface Contract {
  "publication-number"?: string;
  "buyer-name"?: string;
  "buyer-country"?: string;
  "winner-name"?: string;
  "winner-country"?: string;
  "organisation-name-tenderer"?: string;
  "contract-title"?: string;
  "notice-title"?: string;
  "total-value"?: string;
  "total-value-cur"?: string;
  "publication-date"?: string;
  "contract-conclusion-date"?: string;
  "contract-duration-period-lot"?: string;
  "contract-duration-period-part"?: string;
  "duration-period-value-lot"?: string;
  "duration-period-value-part"?: string;
  "duration-period-unit-lot"?: string;
  "duration-period-unit-part"?: string;
  "contract-duration-end-date-lot"?: string;
  "contract-duration-end-date-part"?: string;
  "renewal-maximum-lot"?: string;
  "renewal-description-lot"?: string;
  "classification-cpv"?: string[];
  "estimated-end-date"?: string;
  [key: string]: any;
}

interface RenewalInfo {
  maximumRenewals?: string | null;
  renewalDescription?: string | null;
  hasRenewalInfo: boolean;
}

interface ContractDetailData {
  description?: string;
  procedureType?: string;
  deadlineDate?: string;
  estimatedValue?: string;
  location?: string;
  additionalInfo?: string;
  renewalInfo?: RenewalInfo;
  [key: string]: any;
}

interface ExpiringContractsTableProps {
  contracts: Contract[];
  isLoading: boolean;
}

interface ExpandableRowProps {
  contract: Contract;
  calculateEndDate: (contract: Contract) => string;
  formatValue: (value?: string | number, currency?: string) => string;
  safeFormatDate: (dateString: string | undefined | null, formatString?: string) => string;
  t: (key: string, options?: any) => string;
  navigate: (path: string) => void;
}

export const ExpiringContractsTable = ({ contracts, isLoading }: ExpiringContractsTableProps) => {
  const { t, i18n } = useTranslation("opportunityFinder");
  const isDanish = i18n.language === 'da';
  const navigate = useNavigate();
  
  // Helper function to safely format a date string
  const safeFormatDate = (dateString: string | null | undefined, formatString: string = "d MMM yyyy"): string => {
    if (!dateString) return t("unknown");
    
    try {
      const parsedDate = safeParseDate(dateString);
      
      if (parsedDate && isValid(parsedDate)) {
        return format(parsedDate, formatString, { locale: isDanish ? da : undefined });
      }
      
      return t("unknown");
    } catch (error) {
      console.error("Error formatting date:", error, "Date string:", dateString);
      return t("unknown");
    }
  };

  // Helper function to calculate estimated end date
  const calculateEndDate = (contract: Contract): string => {
    // First check for explicit end dates
    if (contract["contract-duration-end-date-lot"] || contract["contract-duration-end-date-part"]) {
      const endDate = contract["contract-duration-end-date-lot"] || contract["contract-duration-end-date-part"];
      try {
        const parsedDate = safeParseDate(endDate);
        if (parsedDate && isValid(parsedDate)) {
          return format(parsedDate, "d MMM yyyy", { locale: isDanish ? da : undefined });
        }
      } catch (error) {
        console.error("Error formatting explicit end date:", error);
      }
    }
    
    // If the estimated end date is already calculated, use it
    if (contract["estimated-end-date"]) {
      try {
        const parsedDate = safeParseDate(contract["estimated-end-date"]);
        if (parsedDate && isValid(parsedDate)) {
          return format(parsedDate, "d MMM yyyy", { locale: isDanish ? da : undefined });
        }
      } catch (error) {
        console.error("Error formatting pre-calculated end date:", error);
      }
    }
    
    // Try to get the conclusion date
    const conclusionDateStr = contract["contract-conclusion-date"];
    if (!conclusionDateStr) return t("unknown");

    // Try to get duration values
    const durationValue = 
      contract["duration-period-value-lot"] || 
      contract["duration-period-value-part"] || 
      null;
    
    const durationUnit = 
      contract["duration-period-unit-lot"] || 
      contract["duration-period-unit-part"] || 
      null;

    if (!durationValue || !durationUnit) {
      return t("unknown");
    }

    try {
      // Parse conclusion date
      const conclusionDate = safeParseDate(conclusionDateStr);
      if (!conclusionDate || !isValid(conclusionDate)) {
        return t("unknown");
      }
      
      // Calculate the end date based on duration unit and value
      const endDate = new Date(conclusionDate);
      const durationNum = parseInt(durationValue);
      
      if (isNaN(durationNum)) {
        return t("unknown");
      }
      
      if (durationUnit.includes("MONTH")) {
        endDate.setMonth(endDate.getMonth() + durationNum);
      } else if (durationUnit.includes("YEAR")) {
        endDate.setFullYear(endDate.getFullYear() + durationNum);
      } else if (durationUnit.includes("DAY")) {
        endDate.setDate(endDate.getDate() + durationNum);
      }
      
      // Check if the calculated date is valid
      if (!isValid(endDate)) {
        console.error("Calculated end date is invalid:", endDate);
        return t("unknown");
      }
      
      // Format the date with Danish locale if using Danish language
      return format(endDate, "d MMM yyyy", { locale: isDanish ? da : undefined });
    } catch (error) {
      console.error("Error calculating end date:", error);
      return t("unknown");
    }
  };

  // Helper function to format the contract value
  const formatValue = (value?: string | number, currency?: string): string => {
    if (!value) return t("unknown");
    
    try {
      return formatCurrency(value, currency || 'EUR', isDanish ? 'da-DK' : 'en-US');
    } catch (error) {
      console.error("Error formatting value:", error);
      return `${value} ${currency || ''}`;
    }
  };

  if (isLoading) {
    return (
      <Card className="p-4">
        <div className="space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      </Card>
    );
  }

  if (contracts.length === 0) {
    return (
      <Card className="p-6 text-center">
        <p className="text-muted-foreground">{t("noContractsFound")}</p>
      </Card>
    );
  }

  return (
    <Card className="border">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead style={{ width: '25%' }} className="min-w-[200px]">{t("table.title")}</TableHead>
              <TableHead style={{ width: '15%' }} className="min-w-[130px]">{t("table.supplier")}</TableHead>
              <TableHead style={{ width: '15%' }} className="min-w-[130px]">{t("table.buyer")}</TableHead>
              <TableHead style={{ width: '12%' }} className="min-w-[100px]">{t("table.conclusionDate")}</TableHead>
              {/* Removed Duration column */}
              <TableHead style={{ width: '13%' }} className="min-w-[110px]">{t("table.estimatedEndDate")}</TableHead>
              <TableHead style={{ width: '10%' }} className="min-w-[100px]">{t("table.value")}</TableHead>
              <TableHead style={{ width: '10%' }} className="min-w-[80px]">{t("table.cpv")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contracts.map((contract, index) => (
              <ExpandableRow 
                key={`${contract["publication-number"] || ""}-${index}`}
                contract={contract}
                calculateEndDate={calculateEndDate}
                formatValue={formatValue}
                safeFormatDate={safeFormatDate}
                t={t}
                navigate={navigate}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

// New ExpandableRow component to properly handle the collapsible functionality
const ExpandableRow = ({ 
  contract, 
  calculateEndDate, 
  formatValue, 
  safeFormatDate,
  t,
  navigate
}: ExpandableRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [contractDetails, setContractDetails] = useState<ContractDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { i18n } = useTranslation("opportunityFinder");
  const isDanish = i18n.language === 'da';
  
  const publicationNumber = contract["publication-number"] || "";
  
  const toggleExpand = async () => {
    setIsExpanded(!isExpanded);
    
    // Only fetch details if expanding and we don't have them yet
    if (!isExpanded && !contractDetails && publicationNumber) {
      setIsLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("fetch-tender-details", {
          body: { publicationNumber }
        });
        
        if (error) {
          console.error("Error fetching tender details:", error);
          return;
        }
        
        // Extract renewal information
        const renewalInfo = extractRenewalInfo(data);
        
        // Extract business opportunity relevant information
        setContractDetails({
          description: extractDescription(data),
          procedureType: extractProcedureType(data),
          deadlineDate: extractDeadline(data),
          estimatedValue: extractValue(data),
          location: extractLocation(data),
          additionalInfo: extractAdditionalInfo(data),
          renewalInfo: renewalInfo,
          // Store full data for reference
          fullData: data
        });
      } catch (error) {
        console.error("Error fetching contract details:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  // Helper functions to extract relevant information from tender details
  const extractRenewalInfo = (data: any): RenewalInfo => {
    const renewalInfo: RenewalInfo = {
      hasRenewalInfo: false
    };
    
    // Extract maximum renewals
    if (data["renewal-maximum-lot"]) {
      renewalInfo.maximumRenewals = Array.isArray(data["renewal-maximum-lot"]) 
        ? data["renewal-maximum-lot"][0] 
        : data["renewal-maximum-lot"];
      renewalInfo.hasRenewalInfo = true;
    }
    
    // Extract renewal description
    if (data["renewal-description-lot"]) {
      const renewalDesc = data["renewal-description-lot"];
      
      // Handle multilingual description
      if (typeof renewalDesc === 'object' && !Array.isArray(renewalDesc)) {
        const langs = Object.keys(renewalDesc);
        if (langs.length > 0) {
          const lang = langs[0];
          renewalInfo.renewalDescription = Array.isArray(renewalDesc[lang]) 
            ? renewalDesc[lang][0] 
            : renewalDesc[lang];
          renewalInfo.hasRenewalInfo = true;
        }
      } else if (typeof renewalDesc === 'string') {
        renewalInfo.renewalDescription = renewalDesc;
        renewalInfo.hasRenewalInfo = true;
      } else if (Array.isArray(renewalDesc) && renewalDesc.length > 0) {
        renewalInfo.renewalDescription = renewalDesc[0];
        renewalInfo.hasRenewalInfo = true;
      }
    }
    
    return renewalInfo;
  };
  
  const extractDescription = (data: any): string => {
    if (data["BT-24-Procedure"]) {
      const langs = Object.keys(data["BT-24-Procedure"]);
      if (langs.length > 0) {
        return data["BT-24-Procedure"][langs[0]];
      }
    }
    
    if (data["BT-24-Lot"]) {
      const langs = Object.keys(data["BT-24-Lot"]);
      if (langs.length > 0) {
        return Array.isArray(data["BT-24-Lot"][langs[0]]) 
          ? data["BT-24-Lot"][langs[0]][0] 
          : data["BT-24-Lot"][langs[0]];
      }
    }
    
    if (data["description-lot"]) {
      const langs = Object.keys(data["description-lot"]);
      if (langs.length > 0) {
        return Array.isArray(data["description-lot"][langs[0]]) 
          ? data["description-lot"][langs[0]][0] 
          : data["description-lot"][langs[0]];
      }
    }
    
    return t("unknown");
  };
  
  const extractProcedureType = (data: any): string => {
    if (data["procedure-type"]) {
      return data["procedure-type"];
    }
    return t("unknown");
  };
  
  const extractDeadline = (data: any): string => {
    if (data["deadline-receipt-tender-date-lot"]) {
      return Array.isArray(data["deadline-receipt-tender-date-lot"]) 
        ? data["deadline-receipt-tender-date-lot"][0] 
        : data["deadline-receipt-tender-date-lot"];
    }
    
    if (data["deadline-receipt-request"]) {
      return Array.isArray(data["deadline-receipt-request"]) 
        ? data["deadline-receipt-request"][0] 
        : data["deadline-receipt-request"];
    }
    
    return "";
  };
  
  const extractValue = (data: any): string => {
    // First try getting the value from total-value
    if (data["total-value"] && data["total-value-cur"]) {
      return `${data["total-value"]} ${data["total-value-cur"]}`;
    }
    
    // Then try from estimated-value-proc
    if (data["estimated-value-proc"] && data["estimated-value-cur-proc"]) {
      return `${data["estimated-value-proc"]} ${data["estimated-value-cur-proc"]}`;
    }
    
    // Then try from estimated-value-lot
    if (data["estimated-value-lot"] && data["estimated-value-cur-lot"]) {
      if (Array.isArray(data["estimated-value-lot"]) && Array.isArray(data["estimated-value-cur-lot"])) {
        return `${data["estimated-value-lot"][0]} ${data["estimated-value-cur-lot"][0]}`;
      } else {
        return `${data["estimated-value-lot"]} ${data["estimated-value-cur-lot"]}`;
      }
    }
    
    // Try TV field (total value)
    if (data["TV"] && data["TV_CUR"]) {
      return `${data["TV"]} ${data["TV_CUR"]}`;
    }
    
    return "";
  };
  
  const extractLocation = (data: any): string => {
    if (data["place-of-performance-country-lot"]) {
      return Array.isArray(data["place-of-performance-country-lot"]) 
        ? data["place-of-performance-country-lot"][0] 
        : data["place-of-performance-country-lot"];
    }
    
    if (data["buyer-country"]) {
      return data["buyer-country"];
    }
    
    return "";
  };
  
  const extractAdditionalInfo = (data: any): string => {
    const info = [];
    
    if (data["notice-type"]) {
      info.push(`Notice Type: ${data["notice-type"]}`);
    }
    
    if (data["publication-date"]) {
      info.push(`Published: ${safeFormatDate(data["publication-date"])}`);
    }
    
    return info.join("\n");
  };

  // Determine the duration text
  const getDurationText = () => {
    const durationValue = contract["duration-period-value-lot"] || contract["duration-period-value-part"];
    const durationUnit = contract["duration-period-unit-lot"] || contract["duration-period-unit-part"] || "";
    
    if (!durationValue) return t("unknown");
    
    let unitKey = "";
    if (durationUnit.toLowerCase().includes("month")) unitKey = "month";
    else if (durationUnit.toLowerCase().includes("year")) unitKey = "year";
    else if (durationUnit.toLowerCase().includes("day")) unitKey = "day";
    else if (durationUnit.toLowerCase().includes("week")) unitKey = "week";
    
    return `${durationValue} ${t(`durationUnits.${unitKey}`)}`;
  };

  return (
    <>
      {/* Main row - always visible */}
      <TableRow 
        className={`cursor-pointer hover:bg-accent ${isExpanded ? 'bg-muted/30' : ''}`}
        onClick={toggleExpand}
      >
        <TableCell style={{ width: '25%' }} className="min-w-[200px]">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="line-clamp-2">
              {contract["contract-title"] || contract["notice-title"] || t("unknown")}
            </span>
            {isExpanded ? 
              <ChevronUp className="h-4 w-4 ml-1 text-muted-foreground" /> : 
              <ChevronDown className="h-4 w-4 ml-1 text-muted-foreground" />
            }
          </div>
          {publicationNumber && (
            <div className="text-xs text-muted-foreground mt-1">
              {publicationNumber}
            </div>
          )}
        </TableCell>
        <TableCell style={{ width: '15%' }} className="min-w-[130px]">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="line-clamp-2">
              {contract["winner-name"] || contract["organisation-name-tenderer"] || t("unknown")}
            </span>
          </div>
          {contract["winner-country"] && (
            <div className="text-xs text-muted-foreground mt-1">
              {contract["winner-country"]}
            </div>
          )}
        </TableCell>
        <TableCell style={{ width: '15%' }} className="min-w-[130px]">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="line-clamp-2">
              {contract["buyer-name"] || t("unknown")}
            </span>
          </div>
          {contract["buyer-country"] && (
            <div className="text-xs text-muted-foreground mt-1">
              {contract["buyer-country"]}
            </div>
          )}
        </TableCell>
        <TableCell style={{ width: '12%' }} className="min-w-[100px]">
          {contract["contract-conclusion-date"] 
            ? safeFormatDate(contract["contract-conclusion-date"])
            : contract["publication-date"] 
              ? safeFormatDate(contract["publication-date"])
              : t("unknown")}
        </TableCell>
        {/* Removed Duration cell */}
        <TableCell style={{ width: '13%' }} className="min-w-[110px]">
          {calculateEndDate(contract)}
        </TableCell>
        <TableCell style={{ width: '10%' }} className="min-w-[100px]">
          {formatValue(contract["total-value"], contract["total-value-cur"])}
        </TableCell>
        <TableCell style={{ width: '10%' }} className="min-w-[80px]">
          <div className="flex flex-wrap gap-1">
            <TooltipProvider>
              {Array.isArray(contract["classification-cpv"]) && contract["classification-cpv"].slice(0, 2).map((cpv, idx) => (
                <Tooltip key={`${cpv}-${idx}`}>
                  <TooltipTrigger>
                    <Badge variant="outline" className="text-xs">
                      <Tag className="h-3 w-3 mr-1" />
                      {cpv}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    {cpv}
                  </TooltipContent>
                </Tooltip>
              ))}
              {Array.isArray(contract["classification-cpv"]) && contract["classification-cpv"].length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{contract["classification-cpv"].length - 2}
                </Badge>
              )}
              {(!Array.isArray(contract["classification-cpv"]) || contract["classification-cpv"].length === 0) && (
                <span className="text-xs text-muted-foreground">{t("unknown")}</span>
              )}
            </TooltipProvider>
          </div>
        </TableCell>
      </TableRow>

      {/* Expanded detail content - shown when expanded */}
      {isExpanded && (
        <TableRow className="bg-muted/10 border-b-0">
          <TableCell colSpan={8} className="pb-6 pt-0 px-6">
            <div className="mt-2 rounded-md bg-muted/30 p-4">
              {isLoading ? (
                <div className="flex justify-center items-center py-4">
                  <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
                  <span className="ml-2 text-sm text-muted-foreground">Loading contract details...</span>
                </div>
              ) : contractDetails ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                        <FileText className="h-5 w-5 text-primary" />
                        {isDanish ? 'Kontraktmulighed' : 'Contract Opportunity'}
                      </h3>
                      
                      {contractDetails.description && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-muted-foreground mb-1">
                            {isDanish ? 'Beskrivelse' : 'Description'}:
                          </p>
                          <p className="text-sm">{contractDetails.description}</p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        {contractDetails.procedureType && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground">
                              {isDanish ? 'Procedure Type' : 'Procedure Type'}:
                            </p>
                            <p className="text-sm font-medium">{contractDetails.procedureType}</p>
                          </div>
                        )}
                        
                        {contractDetails.location && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground">
                              {isDanish ? 'Lokation' : 'Location'}:
                            </p>
                            <p className="text-sm font-medium">{contractDetails.location}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                        <BarChart className="h-5 w-5 text-primary" />
                        {isDanish ? 'Nøgledetaljer' : 'Key Details'}
                      </h3>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-start gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">
                              {isDanish ? 'Afslutningsdato' : 'Conclusion Date'}:
                            </p>
                            <p className="text-sm font-medium">
                              {contract["contract-conclusion-date"] 
                                ? safeFormatDate(contract["contract-conclusion-date"])
                                : t("unknown")}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">
                              {isDanish ? 'Varighed' : 'Duration'}:
                            </p>
                            <p className="text-sm font-medium">
                              {getDurationText()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          <DollarSign className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">
                              {isDanish ? 'Værdi' : 'Value'}:
                            </p>
                            <p className="text-sm font-medium">
                              {formatValue(contract["total-value"], contract["total-value-cur"])}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          <Briefcase className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">
                              {isDanish ? 'Nuværende Leverandør' : 'Current Supplier'}:
                            </p>
                            <p className="text-sm font-medium line-clamp-2">
                              {contract["winner-name"] || contract["organisation-name-tenderer"] || t("unknown")}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Renewal information section */}
                      {contractDetails.renewalInfo && contractDetails.renewalInfo.hasRenewalInfo && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex items-start gap-2">
                            <RefreshCw className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1">
                                {isDanish ? 'Fornyelsesmuligheder' : 'Renewal Options'}:
                              </p>
                              
                              {contractDetails.renewalInfo.maximumRenewals && (
                                <p className="text-sm mb-1">
                                  {isDanish ? 'Maksimale fornyelser' : 'Maximum renewals'}: {contractDetails.renewalInfo.maximumRenewals}
                                </p>
                              )}
                              
                              {contractDetails.renewalInfo.renewalDescription && (
                                <p className="text-sm">
                                  {contractDetails.renewalInfo.renewalDescription}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex justify-end mt-4 pt-4 border-t">
                    <Button onClick={(e) => {
                      e.stopPropagation(); // Prevent toggling expansion on button click
                      navigate(`/tender-view/${publicationNumber}`);
                    }}>
                      {isDanish ? 'Se Fuld Detaljeret Visning' : 'View Full Details'}
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-4">
                  <p>{isDanish ? 'Ingen detaljer tilgængelige' : 'No details available'}</p>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
